import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Add,
    Delete,
    Edit,
    NetworkCheck,
    Refresh,
} from '@mui/icons-material';
import Layout from './Layout';
import {
    deletePrintWorkflowClient,
    deletePrintWorkflowRow,
    getPrintWorkflowConfig,
    savePrintWorkflowClient,
    savePrintWorkflowRow,
    testPrintWorkflowConnections,
} from '../services/api';
import { useSnackbar } from '../contexts/SnackbarContext';

const DEFAULT_PATTERN = '^(.+?)-qty([1-9][0-9]*)-artwork[.]pdf$';
const getErrorMessage = (error) => error?.response?.data?.message || error?.message || 'Request failed.';

const emptyClient = {
    slug: '',
    name: '',
    sftpRoot: '/transfer/',
    processingRoot: '/transfer/PROCESSING',
    doneRoot: '/transfer/DONE',
    fieryWorkflowPreset: '',
    enabled: true,
};

const emptyRow = {
    clientId: '',
    name: '',
    sourceFolder: '',
    filenamePattern: DEFAULT_PATTERN,
    sampleFilename: 'KX_sample-qty2-artwork.pdf',
    jobSpec: '',
    sortOrder: 0,
    enabled: true,
};

const PrintWorkflowAdmin = () => {
    const { showSnackbar } = useSnackbar();
    const [clients, setClients] = useState([]);
    const [configError, setConfigError] = useState('');
    const [loading, setLoading] = useState(true);
    const [clientForm, setClientForm] = useState(null);
    const [rowForm, setRowForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [connections, setConnections] = useState(null);
    const [testing, setTesting] = useState(false);

    const loadConfig = useCallback(async () => {
        setLoading(true);
        setConfigError('');
        try {
            const data = await getPrintWorkflowConfig();
            if (!data || !Array.isArray(data.clients)) {
                throw new Error('The print workflow API returned an invalid configuration response.');
            }
            setClients(data.clients || []);
        } catch (error) {
            const message = getErrorMessage(error);
            setClients([]);
            setConfigError(message);
            showSnackbar(message, 'error');
        } finally {
            setLoading(false);
        }
    }, [showSnackbar]);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    const testConnections = async () => {
        setTesting(true);
        try {
            const data = await testPrintWorkflowConnections();
            setConnections(data.connections);
            const pressesOk = (data.connections.presses || []).every((press) => press.ok);
            const allOk = data.connections.sftp.ok && (data.connections.presses?.length
                ? pressesOk
                : data.connections.dfe.ok);
            showSnackbar(
                allOk
                    ? 'SFTP and press connections passed.'
                    : 'One or more connection tests failed.',
                allOk ? 'success' : 'warning'
            );
        } catch (error) {
            showSnackbar(getErrorMessage(error), 'error');
        } finally {
            setTesting(false);
        }
    };

    const submitClient = async () => {
        setSaving(true);
        try {
            const data = await savePrintWorkflowClient(clientForm, clientForm.clientId);
            setClients(data.clients || []);
            setClientForm(null);
            showSnackbar('Print workflow client saved.', 'success');
        } catch (error) {
            showSnackbar(getErrorMessage(error), 'error');
        } finally {
            setSaving(false);
        }
    };

    const submitRow = async () => {
        setSaving(true);
        try {
            const data = await savePrintWorkflowRow(rowForm, rowForm.rowId);
            setClients(data.clients || []);
            setRowForm(null);
            showSnackbar('Print workflow row saved.', 'success');
        } catch (error) {
            showSnackbar(getErrorMessage(error), 'error');
        } finally {
            setSaving(false);
        }
    };

    const removeClient = async (client) => {
        if (!window.confirm(`Delete ${client.name}? Disable it instead when history exists.`)) return;
        try {
            const data = await deletePrintWorkflowClient(client.clientId);
            setClients(data.clients || []);
            showSnackbar('Client deleted.', 'success');
        } catch (error) {
            showSnackbar(getErrorMessage(error), 'error');
        }
    };

    const removeRow = async (row) => {
        if (!window.confirm(`Delete ${row.name}? Disable it instead when history exists.`)) return;
        try {
            const data = await deletePrintWorkflowRow(row.rowId);
            setClients(data.clients || []);
            showSnackbar('Workflow row deleted.', 'success');
        } catch (error) {
            showSnackbar(getErrorMessage(error), 'error');
        }
    };

    return (
        <Layout>
            <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        justifyContent="space-between"
                    >
                        <Box>
                            <Typography variant="h5" fontWeight={750}>Print Workflow Settings</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Configure client queues, client-level Fiery workflow presets, and row-level HP JobSpec mappings. SFTP and press credentials remain server-side and are never shown here.
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                startIcon={testing ? <CircularProgress size={15} /> : <NetworkCheck />}
                                disabled={testing}
                                onClick={testConnections}
                            >
                                Test connections
                            </Button>
                            <Button variant="outlined" startIcon={<Refresh />} onClick={loadConfig}>Refresh</Button>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => setClientForm({ ...emptyClient })}
                            >
                                Add client
                            </Button>
                        </Stack>
                    </Stack>
                    {connections && (
                        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} useFlexGap flexWrap="wrap">
                            <Chip
                                label={`SFTP: ${connections.sftp.ok ? 'Connected' : connections.sftp.message || 'Failed'}`}
                                color={connections.sftp.ok ? 'success' : 'error'}
                            />
                            {(connections.presses || []).map((press) => (
                                <Chip
                                    key={press.slug}
                                    label={`${press.name}: ${press.ok ? 'Connected' : press.message || 'Failed'}`}
                                    color={press.ok ? 'success' : 'error'}
                                />
                            ))}
                            {!connections.presses?.length && (
                                <Chip
                                    label={`Production Pro: ${connections.dfe.ok ? 'Connected' : connections.dfe.message || 'Failed'}`}
                                    color={connections.dfe.ok ? 'success' : 'error'}
                                />
                            )}
                        </Stack>
                    )}
                </Paper>

                {configError && <Alert severity="error">{configError}</Alert>}

                {loading ? (
                    <Paper variant="outlined" sx={{ minHeight: 240, display: 'grid', placeItems: 'center' }}>
                        <CircularProgress />
                    </Paper>
                ) : clients.map((client) => (
                    <Paper key={client.clientId} variant="outlined" sx={{ overflow: 'hidden' }}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'stretch', md: 'center' }}
                            spacing={1}
                            sx={{ p: 1.5, bgcolor: 'action.hover' }}
                        >
                            <Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="h6" fontWeight={700}>{client.name}</Typography>
                                    <Chip size="small" label={client.slug} variant="outlined" />
                                    <Chip size="small" label={client.enabled ? 'Enabled' : 'Disabled'} color={client.enabled ? 'success' : 'default'} />
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                    Root: {client.sftpRoot} · Processing: {client.processingRoot} · DONE: {client.doneRoot}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Fiery workflow/preset: {client.fieryWorkflowPreset || 'Not configured'}
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={0.5}>
                                <Button
                                    size="small"
                                    startIcon={<Add />}
                                    onClick={() => setRowForm({ ...emptyRow, clientId: client.clientId })}
                                >
                                    Add row
                                </Button>
                                <Tooltip title="Edit client">
                                    <IconButton size="small" onClick={() => setClientForm({ ...client })}><Edit /></IconButton>
                                </Tooltip>
                                <Tooltip title="Delete client">
                                    <IconButton size="small" color="error" onClick={() => removeClient(client)}><Delete /></IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Product row</TableCell>
                                        <TableCell>Source folder</TableCell>
                                        <TableCell>Filename pattern</TableCell>
                                        <TableCell>Production Pro JobSpec</TableCell>
                                        <TableCell align="right">Order</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {client.rows.map((row) => (
                                        <TableRow key={row.rowId}>
                                            <TableCell>{row.name}</TableCell>
                                            <TableCell>{row.sourceFolder}</TableCell>
                                            <TableCell>
                                                <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                                                    {row.filenamePattern}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {row.jobSpec || <Chip size="small" color="warning" label="Required" />}
                                            </TableCell>
                                            <TableCell align="right">{row.sortOrder}</TableCell>
                                            <TableCell>
                                                <Chip size="small" label={row.enabled ? 'Enabled' : 'Disabled'} color={row.enabled ? 'success' : 'default'} />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setRowForm({
                                                        ...row,
                                                        clientId: client.clientId,
                                                        sampleFilename: 'KX_sample-qty2-artwork.pdf',
                                                    })}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => removeRow(row)}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {!client.rows.length && (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                                No product rows configured.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                ))}
                {!loading && !clients.length && <Alert severity="info">No print workflow clients configured.</Alert>}
            </Stack>

            <Dialog open={Boolean(clientForm)} onClose={() => setClientForm(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{clientForm?.clientId ? 'Edit client' : 'Add client'}</DialogTitle>
                <DialogContent>
                    {clientForm && (
                        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                            <TextField
                                label="Client name"
                                value={clientForm.name}
                                onChange={(event) => setClientForm((current) => ({ ...current, name: event.target.value }))}
                                required
                            />
                            <TextField
                                label="Client slug"
                                value={clientForm.slug}
                                onChange={(event) => setClientForm((current) => ({ ...current, slug: event.target.value.toLowerCase() }))}
                                helperText="Lowercase letters, numbers and hyphens."
                                required
                            />
                            <TextField
                                label="SFTP root"
                                value={clientForm.sftpRoot}
                                onChange={(event) => setClientForm((current) => ({ ...current, sftpRoot: event.target.value }))}
                                required
                            />
                            <TextField
                                label="PROCESSING root"
                                value={clientForm.processingRoot}
                                onChange={(event) => setClientForm((current) => ({ ...current, processingRoot: event.target.value }))}
                                required
                            />
                            <TextField
                                label="DONE root"
                                value={clientForm.doneRoot}
                                onChange={(event) => setClientForm((current) => ({ ...current, doneRoot: event.target.value }))}
                                required
                            />
                            <TextField
                                label="Fiery workflow / server preset"
                                value={clientForm.fieryWorkflowPreset || ''}
                                onChange={(event) => setClientForm((current) => ({
                                    ...current,
                                    fieryWorkflowPreset: event.target.value,
                                }))}
                                helperText="Client-level Fiery server preset used for every Fuji/Revoria submission. The name must exactly match Fiery, for example Bisctuiteers_Sleeves."
                            />
                            <FormControlLabel
                                control={(
                                    <Switch
                                        checked={clientForm.enabled}
                                        onChange={(event) => setClientForm((current) => ({ ...current, enabled: event.target.checked }))}
                                    />
                                )}
                                label="Enabled"
                            />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClientForm(null)}>Cancel</Button>
                    <Button variant="contained" disabled={saving} onClick={submitClient}>
                        {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(rowForm)} onClose={() => setRowForm(null)} maxWidth="md" fullWidth>
                <DialogTitle>{rowForm?.rowId ? 'Edit product row' : 'Add product row'}</DialogTitle>
                <DialogContent>
                    {rowForm && (
                        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                            <TextField
                                label="Display name"
                                value={rowForm.name}
                                onChange={(event) => setRowForm((current) => ({ ...current, name: event.target.value }))}
                                required
                            />
                            <TextField
                                label="Source folder (relative to client root)"
                                value={rowForm.sourceFolder}
                                onChange={(event) => setRowForm((current) => ({ ...current, sourceFolder: event.target.value }))}
                                required
                            />
                            <TextField
                                label="Filename regular expression"
                                value={rowForm.filenamePattern}
                                onChange={(event) => setRowForm((current) => ({ ...current, filenamePattern: event.target.value }))}
                                helperText="Capture item ID in group 1 and quantity in group 2."
                                required
                            />
                            <TextField
                                label="Validation sample filename"
                                value={rowForm.sampleFilename}
                                onChange={(event) => setRowForm((current) => ({ ...current, sampleFilename: event.target.value }))}
                                required
                            />
                            <TextField
                                label="HP Production Pro JobSpec / Internet Input Method"
                                value={rowForm.jobSpec}
                                onChange={(event) => setRowForm((current) => ({ ...current, jobSpec: event.target.value }))}
                                helperText="Row-level setting used only when submitting to HP Indigo."
                            />
                            <TextField
                                label="Sort order"
                                type="number"
                                value={rowForm.sortOrder}
                                onChange={(event) => setRowForm((current) => ({ ...current, sortOrder: event.target.value }))}
                            />
                            <FormControlLabel
                                control={(
                                    <Switch
                                        checked={rowForm.enabled}
                                        onChange={(event) => setRowForm((current) => ({ ...current, enabled: event.target.checked }))}
                                    />
                                )}
                                label="Enabled"
                            />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRowForm(null)}>Cancel</Button>
                    <Button variant="contained" disabled={saving} onClick={submitRow}>
                        {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Layout>
    );
};

export default PrintWorkflowAdmin;
